/**
 * ============================================
 * PROTECTED ROUTE - Ruta Protegida
 * ============================================
 *
 * Componente que protege rutas que requieren autenticación.
 * Si el usuario no está autenticado, lo redirige a /login.
 *
 * USO:
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * O en el router:
 * {
 *   path: "/",
 *   element: <ProtectedRoute><RootLayout /></ProtectedRoute>,
 *   children: [...]
 * }
 */

import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  /** Contenido a mostrar si está autenticado */
  children: React.ReactNode;
}

/**
 * ProtectedRoute
 *
 * Flujo:
 * 1. Si isLoading=true → Mostrar spinner (verificando sesión)
 * 2. Si isAuthenticated=false → Redirigir a /login
 * 3. Si isAuthenticated=true → Mostrar children
 *
 * El componente Navigate de react-router-dom hace la redirección.
 * - replace: true → Reemplaza la entrada en el historial (no se puede volver atrás)
 * - state: { from } → Guarda la ruta original para redirigir después del login
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Mientras se verifica la sesión inicial, mostrar loading
  // Esto evita un "flash" de redirección a login mientras se valida el token
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  // Si no está autenticado, redirigir a login
  // Guardamos la ubicación actual para poder redirigir de vuelta después del login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Usuario autenticado - mostrar el contenido protegido
  return <>{children}</>;
}
